import React, {
  useState,
  useMemo,
  ReactElement,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import ModalDialog, { ModalTransition } from '@atlaskit/modal-dialog';
import Row from 'components/Common/Row';
import Loading from '../../../../../components/Loading';
import ToastContext from '../../../../../contexts/ToastContext';
import captureException from 'services/captureException';
import Button from 'kingpin/atoms/Button';
import Form from 'kingpin/forms/Form';
import FormContent from 'kingpin/forms/FormContent';
import FormHeader from 'kingpin/forms/FormHeader';
import FindAndReplaceSelector from './FindAndReplaceSelector';
import {
  FindAndReplaceContext,
  FindAndReplaceProvider,
} from './FindAndReplace';
import FindAndReplaceUsage from './FindAndReplaceUsage';
import useFindAndReplace from './useFindAndReplace';
import getUsageCount from './useFindAndReplace/getUsageCount';

const FindAndReplace = ({ close }: { close: () => void }) => {
  const [step, setStep] = useState<'1' | '2'>('1');
  const [usageCount, setUsageCount] = React.useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useContext(ToastContext);
  const {
    findMetricId,
    replaceMetricId,
    selectedAffectedContent,
    isArchiveUnusedMetricSelected,
  } = useContext(FindAndReplaceContext);
  const runMigration = useFindAndReplace();
  useEffect(() => {
    if (selectedAffectedContent?.affectedContentIds) {
      setUsageCount(getUsageCount(selectedAffectedContent?.affectedContentIds));
    }
  }, [selectedAffectedContent?.affectedContentIds]);

  const replace = useCallback(() => {
    setIsLoading(true);
    runMigration(selectedAffectedContent, isArchiveUnusedMetricSelected)
      .then(() => {
        setIsLoading(false);
        showToast('Find and replace complete');
        close();
      })
      .catch((e) => {
        setIsLoading(false);
        showToast('Find and replace failed. Some content may not be updated');
        captureException(e);
      });
  }, [
    close,
    isArchiveUnusedMetricSelected,
    runMigration,
    selectedAffectedContent,
    showToast,
  ]);

  const Body = useMemo((): ReactElement => {
    if (isLoading) {
      return <Loading />;
    }
    if (step === '1') {
      return <FindAndReplaceSelector />;
    }
    return <FindAndReplaceUsage />;
  }, [step, isLoading]);
  return (
    <Form>
      <FormHeader title={'Find & Replace'} onClose={close} />
      <FormContent>{Body}</FormContent>
      <Row spaceBetween>
        {step === '1' && (
          <>
            <Button
              onClick={close}
              type="Secondary"
              size="Small"
              label="Close"
            />
            <Button
              onClick={() => {
                setStep('2');
              }}
              isDisabled={!findMetricId || !replaceMetricId}
              type="Primary"
              size="Small"
              label="Next"
            />
          </>
        )}
        {step === '2' && (
          <>
            <Button
              onClick={() => setStep('1')}
              type="Secondary"
              size="Small"
              label="Back"
            />
            <Button
              onClick={replace}
              isDisabled={
                !findMetricId || !replaceMetricId || !usageCount || isLoading
              }
              type="Primary"
              size="Small"
              label="Replace"
            />
          </>
        )}
      </Row>
    </Form>
  );
};

const Gate = ({ isOpen, close }: { isOpen: boolean; close: () => void }) => (
  <ModalTransition>
    {isOpen && (
      <ModalDialog
        onClose={close}
        shouldScrollInViewport={false}
        autoFocus={false}
      >
        <FindAndReplaceProvider>
          <FindAndReplace close={close} />
        </FindAndReplaceProvider>
      </ModalDialog>
    )}
  </ModalTransition>
);

export default Gate;
